import { message, Select, Tabs } from 'antd';
import { ChevronLeft, ChevronRight, Plus, Send, UserPlus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { ConfirmDialog, confirm } from '@/components/common/ConfirmDialog';
import { Pagination } from '@/components/common/Pagination';
import { SearchBar } from '@/components/common/SearchBar';
import { Button } from '@/components/ui/button';
import {
  betaAccessStore,
  brandStore,
  type InviteCode,
  type InviteCodeApplication,
  inviteApplicationStore,
  inviteCodeStore,
} from '@/stores';
import {
  BetaAccessStatus,
  InviteApplicationStatus,
  InviteCodeStatus,
} from '@/types';
import { highlightKeyword } from '@/utils/helpers';
import { ApproveDialog } from './components/ApproveDialog';
import { BatchSendDialog } from './components/BatchSendDialog';
import { BetaAccessTable } from './components/BetaAccessTable';
import { GenerateDialog } from './components/GenerateDialog';
import { GrantBetaDialog } from './components/GrantBetaDialog';
import { InviteApplicationTable } from './components/InviteApplicationTable';
import { InviteCodeTable } from './components/InviteCodeTable';
import { SendInviteCodeDialog } from './components/SendInviteCodeDialog';

export const BetaManagementPage = observer(() => {
  const [activeTab, setActiveTab] = useState<
    'invite-codes' | 'applications' | 'beta-users'
  >('invite-codes');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [grantBetaDialogOpen, setGrantBetaDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [batchSendOpen, setBatchSendOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState<InviteCode | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedBetaUserId, setSelectedBetaUserId] = useState<string | null>(
    null,
  );
  const [selectedApplication, setSelectedApplication] =
    useState<InviteCodeApplication | null>(null);
  const prevPaginationRef = useRef<{
    appId: string;
    page: number;
    pageSize: number;
    keyword: string;
    statusFilter: InviteCodeStatus | undefined;
  } | null>(null);

  // 邀请码列表：pagination 或 statusFilter 变化时调用 API
  const activeAppId = brandStore.activeBrandAppId;
  const invitePage = inviteCodeStore.pagination.page;
  const invitePageSize = inviteCodeStore.pagination.pageSize;
  const inviteKeyword = inviteCodeStore.searchKeyword;
  const inviteStatusFilter = inviteCodeStore.statusFilter;
  // biome-ignore lint/correctness/useExhaustiveDependencies: MobX observable pagination values intentionally drive the refresh.
  useEffect(() => {
    if (!activeAppId) {
      return;
    }
    inviteCodeStore.setAppScope(activeAppId);
    const prev = prevPaginationRef.current;
    const currentPage = inviteCodeStore.pagination.page;

    if (
      !prev ||
      prev.appId !== activeAppId ||
      prev.page !== currentPage ||
      prev.pageSize !== invitePageSize ||
      prev.keyword !== inviteKeyword ||
      prev.statusFilter !== inviteStatusFilter
    ) {
      prevPaginationRef.current = {
        appId: activeAppId,
        page: currentPage,
        pageSize: invitePageSize,
        keyword: inviteKeyword,
        statusFilter: inviteStatusFilter,
      };
      void inviteCodeStore.fetchInviteCodes().catch((error) => {
        console.error('加载邀请码失败:', error);
        message.error('加载邀请码失败，请重试');
      });
    }
  }, [
    activeAppId,
    invitePage,
    invitePageSize,
    inviteKeyword,
    inviteStatusFilter,
  ]);

  // 申请管理：切换到该 tab 或 pagination/statusFilter 变化时拉取
  const appPage = inviteApplicationStore.pagination.page;
  const appPageSize = inviteApplicationStore.pagination.pageSize;
  const appStatusFilter = inviteApplicationStore.statusFilter;
  // biome-ignore lint/correctness/useExhaustiveDependencies: MobX observable pagination values intentionally drive the refresh.
  useEffect(() => {
    if (activeTab === 'applications') {
      inviteApplicationStore.fetchInviteApplications();
    }
  }, [activeTab, appPage, appPageSize, appStatusFilter]);

  const betaPage = betaAccessStore.pagination.page;
  const betaPageSize = betaAccessStore.pagination.pageSize;
  const betaStatusFilter = betaAccessStore.statusFilter;
  const betaSourceFilter = betaAccessStore.sourceFilter;
  // biome-ignore lint/correctness/useExhaustiveDependencies: MobX observable filters and pagination intentionally drive the refresh.
  useEffect(() => {
    if (activeTab === 'beta-users') {
      betaAccessStore.fetchBetaAccesses();
    }
  }, [activeTab, betaPage, betaPageSize, betaStatusFilter, betaSourceFilter]);

  const handleInviteSearch = (keyword: string) => {
    const wasFirstPage = inviteCodeStore.pagination.page === 1;
    const changed = inviteCodeStore.setSearchKeyword(keyword);
    if (!changed && wasFirstPage) {
      void inviteCodeStore.fetchInviteCodes().catch((error) => {
        console.error('加载邀请码失败:', error);
        message.error('加载邀请码失败，请重试');
      });
    }
  };

  const handleSend = (row: InviteCode) => {
    setSendTarget(row);
    setSendDialogOpen(true);
  };

  const handleBetaUserSearch = (userId: string) => {
    betaAccessStore.setSearchUserId(userId);
    betaAccessStore.fetchBetaAccesses();
  };

  const handleDelete = (id: string) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedId) {
      try {
        await inviteCodeStore.deleteInviteCode(selectedId);
      } catch (error) {
        console.error('删除失败:', error);
      }
    }
    setSelectedId(null);
  };

  const handleApprove = (application: InviteCodeApplication) => {
    setSelectedApplication(application);
    setApproveDialogOpen(true);
  };

  const handleRevoke = (userId: string) => {
    setSelectedBetaUserId(userId);
    setRevokeDialogOpen(true);
  };

  const confirmRevoke = () => {
    if (!selectedBetaUserId) {
      return;
    }

    const userId = selectedBetaUserId;
    confirm({
      title: '再次确认撤销内测资格',
      content: (
        <div className="space-y-3 text-sm">
          <p>
            用户 <span className="font-mono font-medium">{userId}</span>{' '}
            将立即失去内测资格。
          </p>
          <p className="text-muted-foreground">
            如需恢复，需要你手动再次加回去。请再次确认是否继续撤销。
          </p>
        </div>
      ),
      okText: '确认撤销',
      cancelText: '取消',
      type: 'error',
      onOk: async () => {
        const switchedToAll =
          betaAccessStore.statusFilter ===
          BetaAccessStatus.BETA_ACCESS_STATUS_ACTIVE;

        try {
          await betaAccessStore.revokeBetaAccess(userId);
          message.success(
            switchedToAll
              ? `已撤销用户 ${userId} 的内测资格，列表已切换为全部状态`
              : `已撤销用户 ${userId} 的内测资格`,
          );
        } catch (error) {
          console.error('撤销内测资格失败:', error);
          message.error('撤销内测资格失败，请重试');
        } finally {
          setSelectedBetaUserId(null);
        }
      },
      onCancel: () => {
        setSelectedBetaUserId(null);
      },
    });
  };

  const handleRestore = async (userId: string) => {
    const switchedToAll =
      betaAccessStore.statusFilter ===
      BetaAccessStatus.BETA_ACCESS_STATUS_REVOKED;

    try {
      await betaAccessStore.grantBetaAccess({ user_id: userId });
      message.success(
        switchedToAll
          ? `已重新为用户 ${userId} 开通内测资格，列表已切换为全部状态`
          : `已重新为用户 ${userId} 开通内测资格`,
      );
    } catch (error) {
      console.error('恢复内测资格失败:', error);
      message.error('恢复内测资格失败，请重试');
    }
  };

  const selectedInviteCode = selectedId
    ? inviteCodeStore.inviteCodes.find((code) => code.id === selectedId)
    : null;

  const inviteCodeStatusFilterOptions = [
    { value: 'all', label: '全部' },
    {
      value: InviteCodeStatus.INVITE_CODE_STATUS_ACTIVE,
      label: '可用',
    },
    {
      value: InviteCodeStatus.INVITE_CODE_STATUS_USED,
      label: '已使用',
    },
    {
      value: InviteCodeStatus.INVITE_CODE_STATUS_EXPIRED,
      label: '已过期',
    },
    {
      value: InviteCodeStatus.INVITE_CODE_STATUS_DELETED,
      label: '已删除',
    },
  ];

  const statusFilterOptions = [
    { value: 'all', label: '全部' },
    {
      value: InviteApplicationStatus.INVITE_APPLICATION_STATUS_PENDING,
      label: '未处理',
    },
    {
      value: InviteApplicationStatus.INVITE_APPLICATION_STATUS_EMAIL_SENT,
      label: '已发送邮件',
    },
    {
      value: InviteApplicationStatus.INVITE_APPLICATION_STATUS_REJECTED,
      label: '已拒绝',
    },
  ];

  const betaStatusFilterOptions = [
    { value: 'all', label: '全部' },
    {
      value: BetaAccessStatus.BETA_ACCESS_STATUS_ACTIVE,
      label: '已开通',
    },
    {
      value: BetaAccessStatus.BETA_ACCESS_STATUS_REVOKED,
      label: '已撤销',
    },
  ];

  const betaSourceFilterOptions = [
    { value: 'all', label: '全部来源' },
    { value: 'admin', label: '手动开通' },
    { value: 'waitlist', label: 'Waitlist' },
    { value: 'invite_code', label: '邀请码' },
  ];

  return (
    <div className="bg-white rounded-lg border border-border shadow-sm">
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-foreground">内测管理</h1>
          {activeTab === 'invite-codes' && (
            <Button onClick={() => setGenerateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              生成邀请码
            </Button>
          )}
          {activeTab === 'beta-users' && (
            <div className="flex gap-2">
              <Button onClick={() => setGrantBetaDialogOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                给用户加内测资格
              </Button>
              <Button variant="outline" onClick={() => setBatchSendOpen(true)}>
                <Send className="mr-2 h-4 w-4" />
                批量发送
              </Button>
            </div>
          )}
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={(key) =>
            setActiveTab(key as 'invite-codes' | 'applications' | 'beta-users')
          }
          items={[
            {
              key: 'invite-codes',
              label: '邀请码管理',
              children: (
                <>
                  <div className="flex flex-wrap items-end gap-4 mb-4">
                    <SearchBar
                      placeholder="搜索邀请码..."
                      onSearch={handleInviteSearch}
                      className="max-w-md"
                    />
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        状态筛选
                      </span>
                      <Select
                        value={inviteCodeStore.statusFilter ?? 'all'}
                        onChange={(value) =>
                          inviteCodeStore.setStatusFilter(
                            value === 'all'
                              ? undefined
                              : (value as InviteCodeStatus),
                          )
                        }
                        options={inviteCodeStatusFilterOptions}
                        style={{ width: 140 }}
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <InviteCodeTable
                      onDelete={handleDelete}
                      onSend={handleSend}
                    />
                    {(inviteCodeStore.pagination.page > 1 ||
                      inviteCodeStore.pagination.total >
                        inviteCodeStore.pagination.pageSize) && (
                      <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
                        <span>共 {inviteCodeStore.pagination.total} 条</span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={
                            inviteCodeStore.loading ||
                            inviteCodeStore.pagination.page <= 1
                          }
                          onClick={() =>
                            inviteCodeStore.setPagination(
                              inviteCodeStore.pagination.page - 1,
                            )
                          }
                        >
                          <ChevronLeft className="mr-1 h-4 w-4" />
                          上一页
                        </Button>
                        <span>第 {inviteCodeStore.pagination.page} 页</span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={
                            inviteCodeStore.loading || !inviteCodeStore.hasMore
                          }
                          onClick={() =>
                            inviteCodeStore.setPagination(
                              inviteCodeStore.pagination.page + 1,
                            )
                          }
                        >
                          下一页
                          <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              ),
            },
            {
              key: 'applications',
              label: 'Waitlist 申请管理',
              children: (
                <>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        状态筛选
                      </span>
                      <Select
                        value={inviteApplicationStore.statusFilter ?? 'all'}
                        onChange={(value) =>
                          inviteApplicationStore.setStatusFilter(
                            value === 'all'
                              ? undefined
                              : (value as InviteApplicationStatus),
                          )
                        }
                        options={statusFilterOptions}
                        style={{ width: 140 }}
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <InviteApplicationTable onApprove={handleApprove} />
                    <Pagination
                      page={inviteApplicationStore.pagination.page}
                      pageSize={inviteApplicationStore.pagination.pageSize}
                      total={inviteApplicationStore.pagination.total}
                      onPageChange={(page) => {
                        inviteApplicationStore.setPagination(page);
                      }}
                    />
                  </div>
                </>
              ),
            },
            {
              key: 'beta-users',
              label: '内测用户',
              children: (
                <>
                  <div className="flex flex-wrap items-end gap-4 mb-4">
                    <SearchBar
                      placeholder="搜索用户 ID..."
                      onSearch={handleBetaUserSearch}
                      className="max-w-md"
                    />
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        状态筛选
                      </span>
                      <Select
                        value={betaAccessStore.statusFilter ?? 'all'}
                        onChange={(value) =>
                          betaAccessStore.setStatusFilter(
                            value === 'all'
                              ? undefined
                              : (value as BetaAccessStatus),
                          )
                        }
                        options={betaStatusFilterOptions}
                        style={{ width: 140 }}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        来源筛选
                      </span>
                      <Select
                        value={betaAccessStore.sourceFilter || 'all'}
                        onChange={(value) =>
                          betaAccessStore.setSourceFilter(
                            value === 'all' ? '' : (value as string),
                          )
                        }
                        options={betaSourceFilterOptions}
                        style={{ width: 160 }}
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <BetaAccessTable
                      onRevoke={handleRevoke}
                      onRestore={handleRestore}
                    />
                    <Pagination
                      page={betaAccessStore.pagination.page}
                      pageSize={betaAccessStore.pagination.pageSize}
                      total={betaAccessStore.pagination.total}
                      onPageChange={(page) => {
                        betaAccessStore.setPagination(page);
                      }}
                    />
                  </div>
                </>
              ),
            },
          ]}
        />
      </div>

      <GenerateDialog
        open={generateDialogOpen}
        onOpenChange={setGenerateDialogOpen}
      />

      <GrantBetaDialog
        open={grantBetaDialogOpen}
        onOpenChange={setGrantBetaDialogOpen}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="删除邀请码"
        description="确定要删除这个邀请码吗？删除后，对应的 userId 将被移除邀请码内容。此操作不可恢复。"
        onConfirm={confirmDelete}
        variant="destructive"
        extraInfo={
          selectedInviteCode?.usedBy ? (
            <div className="space-y-3 rounded-md bg-muted/50 p-4 text-sm border border-border">
              <div className="flex items-start gap-2">
                <span className="font-medium text-muted-foreground whitespace-nowrap">
                  邀请码：
                </span>
                <span className="font-mono font-medium text-text-strong break-all">
                  {highlightKeyword(
                    selectedInviteCode.code,
                    inviteCodeStore.searchKeyword,
                  )}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-medium text-muted-foreground whitespace-nowrap">
                  UserId：
                </span>
                <span className="font-mono text-text-strong break-all">
                  {highlightKeyword(
                    selectedInviteCode.usedBy,
                    inviteCodeStore.searchKeyword,
                  )}
                </span>
              </div>
            </div>
          ) : selectedInviteCode ? (
            <div className="space-y-3 rounded-md bg-muted/50 p-4 text-sm border border-border">
              <div className="flex items-start gap-2">
                <span className="font-medium text-muted-foreground whitespace-nowrap">
                  邀请码：
                </span>
                <span className="font-mono font-medium text-text-strong break-all">
                  {highlightKeyword(
                    selectedInviteCode.code,
                    inviteCodeStore.searchKeyword,
                  )}
                </span>
              </div>
            </div>
          ) : undefined
        }
      />

      <ConfirmDialog
        open={revokeDialogOpen}
        onOpenChange={(open) => {
          setRevokeDialogOpen(open);
          if (!open) {
            setSelectedBetaUserId(null);
          }
        }}
        title="撤销内测资格"
        description={`确定要撤销用户 ${selectedBetaUserId ?? ''} 的内测资格吗？确认后还会再进行一次二次确认。`}
        confirmText="下一步"
        onConfirm={confirmRevoke}
        variant="destructive"
      />

      <ApproveDialog
        open={approveDialogOpen}
        onOpenChange={setApproveDialogOpen}
        application={selectedApplication}
      />

      <SendInviteCodeDialog
        open={sendDialogOpen}
        onOpenChange={setSendDialogOpen}
        inviteCode={sendTarget}
      />
      <BatchSendDialog open={batchSendOpen} onOpenChange={setBatchSendOpen} />
    </div>
  );
});
